import { callOpenAI } from "../supabase/openAI/openAICall";
import { Database } from "types/supabase";

/**
 * Find the venue that most closely matches the provided name
 * @param searchName - The name to search for
 * @param allVenues - List of all available venues
 * @returns The matching venue or null if no match is found
 */
export const findClosestVenueMatch = async (
  searchName: string,
  allVenues: Database["public"]["Tables"]["venues"]["Row"][]
) => {
  if (!searchName || !allVenues.length) return null;

  try {
    // Create the prompt with all venue names and the query
    const venueNames = allVenues.map((venue) => venue.name).join(", ");
    const prompt = `Here is a list of venue names: ${venueNames}.\n\
    Please check if any of these names are very similar to: "${searchName}"?\n\nIf there is - respond with just the exact name from the list. If no match is found, respond with an empty string: "".`;

    // Call OpenAI API via the utility function
    const trimmedName = await callOpenAI(prompt);

    if (!trimmedName) {
      return null;
    }

    // Find the venue with this name
    const exactMatch = allVenues.find((venue) => venue.name === trimmedName);
    if (exactMatch) {
      return exactMatch;
    }

    // If no exact match, try case-insensitive match
    const caseInsensitiveMatch = allVenues.find(
      (venue) => venue.name?.toLowerCase() === trimmedName.toLowerCase()
    );
    if (caseInsensitiveMatch) {
      return caseInsensitiveMatch;
    }

    // As a last resort, look for partial matches
    return (
      allVenues.find(
        (venue) =>
          venue.name?.toLowerCase().includes(trimmedName.toLowerCase()) ||
          trimmedName.toLowerCase().includes(venue.name?.toLowerCase() || "")
      ) || null
    );
  } catch (error) {
    console.error("Error finding venue match:", error);
    return null;
  }
};
